/**
 * Messages d'erreur conviviaux pour les utilisateurs
 * Transforme les erreurs techniques en messages compréhensibles
 */

export const ERROR_MESSAGES = {
  // Erreurs réseau
  NETWORK_ERROR: "Problème de connexion. Vérifiez votre réseau et réessayez.",
  TIMEOUT_ERROR: "La requête a pris trop de temps. Le service est peut-être surchargé.",
  API_UNAVAILABLE: "Le service est temporairement indisponible. Réessayez dans quelques minutes.",
  
  // Erreurs API
  NOT_FOUND: "Les données demandées sont introuvables.",
  SERVER_ERROR: "Une erreur technique s'est produite. Nous travaillons à la résoudre.",
  RATE_LIMITED: "Trop de requêtes. Attendez un moment avant de réessayer.",
  
  // Erreurs de données
  INVALID_DATA: "Les données reçues sont corrompues. Actualisez la page.",
  PARSING_ERROR: "Impossible de traiter les données. Contactez le support si le problème persiste.",
  
  // Erreurs génériques
  UNKNOWN_ERROR: "Une erreur inattendue s'est produite. Réessayez ou contactez le support.",
  
  // Messages contextuels
  PEG_DATA_ERROR: "Impossible de récupérer les données de peg. Les marchés sont peut-être fermés.",
  ASSET_NOT_SUPPORTED: "Cet asset n'est pas encore supporté. Consultez la liste des assets disponibles."
};

/**
 * Transforme une erreur technique en message utilisateur convivial
 */
export function getUserFriendlyError(error) {
  if (!error) return ERROR_MESSAGES.UNKNOWN_ERROR;
  
  const errorMessage = error.message || error.toString();
  const lowerMessage = errorMessage.toLowerCase();
  
  // Erreurs réseau
  if (lowerMessage.includes('network') || lowerMessage.includes('failed to fetch')) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }
  
  if (lowerMessage.includes('timeout') || lowerMessage.includes('aborted')) {
    return ERROR_MESSAGES.TIMEOUT_ERROR;
  }
  
  // Erreurs HTTP
  if (lowerMessage.includes('404') || lowerMessage.includes('not found')) {
    return ERROR_MESSAGES.NOT_FOUND;
  }
  
  if (lowerMessage.includes('500') || lowerMessage.includes('502') || lowerMessage.includes('503')) {
    return ERROR_MESSAGES.SERVER_ERROR;
  }
  
  if (lowerMessage.includes('429') || lowerMessage.includes('rate limit')) {
    return ERROR_MESSAGES.RATE_LIMITED;
  }
  
  // Erreurs spécifiques au domaine
  if (lowerMessage.includes('peg') || lowerMessage.includes('asset')) {
    return ERROR_MESSAGES.PEG_DATA_ERROR;
  }
  
  if (lowerMessage.includes('parsing') || lowerMessage.includes('json')) {
    return ERROR_MESSAGES.PARSING_ERROR;
  }
  
  // Erreur générique si aucune correspondance
  return ERROR_MESSAGES.API_UNAVAILABLE;
}

/**
 * Suggestions d'actions pour les erreurs courantes
 */
export function getErrorActions(error) {
  const userFriendlyError = getUserFriendlyError(error);
  
  const actionMap = {
    [ERROR_MESSAGES.NETWORK_ERROR]: [
      "Vérifiez votre connexion internet",
      "Actualisez la page", 
      "Essayez avec un autre navigateur"
    ],
    [ERROR_MESSAGES.TIMEOUT_ERROR]: [
      "Réessayez dans quelques secondes",
      "Le service est peut-être surchargé"
    ],
    [ERROR_MESSAGES.API_UNAVAILABLE]: [
      "Réessayez dans 2-3 minutes",
      "Consultez notre page de statut"
    ],
    [ERROR_MESSAGES.SERVER_ERROR]: [
      "Le problème sera résolu rapidement", 
      "Contactez le support si cela persiste"
    ]
  };
  
  return actionMap[userFriendlyError] || ["Réessayez ou contactez le support"];
}

/**
 * Composant d'erreur standardisé
 */
export const ErrorDisplay = ({ error, onRetry, className = "" }) => {
  const friendlyMessage = getUserFriendlyError(error);
  const actions = getErrorActions(error);
  
  return (
    <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-red-800">
            Service temporairement indisponible
          </h3>
          <p className="mt-1 text-sm text-red-700">
            {friendlyMessage}
          </p>
          
          <div className="mt-2">
            <ul className="text-xs text-red-600 space-y-1">
              {actions.map((action, index) => (
                <li key={index}>• {action}</li>
              ))}
            </ul>
          </div>
          
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-3 text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded transition-colors"
            >
              Réessayer
            </button>
          )}
        </div>
      </div>
    </div>
  );
};